import { useCallback, useContext, useEffect, useRef } from 'react';
import { PluginListenerHandle } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { useShallow } from 'zustand/react/shallow';

import {
	Action,
	BusinessLayerContext,
	fetchSubscriptions,
	fetchTickets,
	resyncPassengerBookings,
	usePassengerBookingsActions,
} from '@mopla/business-logic';
import { PushNotificationTypeEnum } from '@mopla/data-models';
import { useLogger } from '@mopla/logger';
import { usePassengerRoutingHelpers } from '@mopla/ui';
import { usePendingNotificationsQueueStore } from '@mopla/utils';

/** RxDB is local - the booking is either there at once or not synced at all */
const BOOKING_LOOKUP_TIMEOUT_MS = 1_000;

interface Payload {
	type: PushNotificationTypeEnum;
	bookingNumber?: string;
	bookingId?: string;
	subscriptionId?: string;
}

export function NotificationListener() {
	const businessLayer = useContext(BusinessLayerContext);
	const logger = useLogger('notificationListener');
	const { navigateToTickets } = usePassengerRoutingHelpers();
	const { waitForPassengerBookingById } = usePassengerBookingsActions();
	const { setFeedback, setDelay } = usePendingNotificationsQueueStore(
		useShallow((state) => ({
			setDelay: state.setDelay,
			setFeedback: state.setFeedback,
		}))
	);

	const notificationHandler = useCallback(
		(payload: Payload) => {
			const { bookingNumber, subscriptionId, bookingId } = payload;

			const isDelay =
				bookingNumber && payload.type === PushNotificationTypeEnum.TRIP_DELAYED;
			const isFeedback =
				bookingId && payload.type === PushNotificationTypeEnum.TRIP_FEEDBACK;

			// Passenger's booking related actions should be handled
			// in the <PassengerNotifications /> component
			if (isDelay) {
				businessLayer.dispatch(resyncPassengerBookings() as Action);
				setDelay({ bookingNumber });
			}

			if (isFeedback) {
				setFeedback({ bookingId });
			}

			if (subscriptionId) {
				businessLayer.dispatch(fetchSubscriptions());
				businessLayer.dispatch(fetchTickets());
			}
		},
		[businessLayer, setDelay, setFeedback]
	);

	/** Tray tap opens the activity the push is about */
	const navigateToPushTarget = useCallback(
		async ({ bookingNumber, bookingId }: Payload) => {
			if (bookingNumber) {
				navigateToTickets({ bookingNumber });
				return;
			}

			if (!bookingId) {
				return;
			}

			/** Pushes carrying only the id: resolve the number the activity route needs */
			try {
				const booking = await waitForPassengerBookingById(bookingId, {
					timeoutDuration: BOOKING_LOOKUP_TIMEOUT_MS,
				});

				navigateToTickets({ bookingNumber: booking.bookingNumber });
			} catch (err) {
				/** Booking gone or never synced - leave the user where they are */
				logger.error('Push target booking not resolved', err, { bookingId });
			}
		},
		[navigateToTickets, waitForPassengerBookingById, logger]
	);

	/** Push notification listeners subscribe once and read the current handlers from this ref */
	const handlersRef = useRef({ notificationHandler, navigateToPushTarget });

	useEffect(() => {
		handlersRef.current = { notificationHandler, navigateToPushTarget };
	}, [notificationHandler, navigateToPushTarget]);

	useEffect(() => {
		/** {setupListeners} is async - a teardown before it resolves has nothing to remove yet */
		let isUnmounted = false;
		const listeners: PluginListenerHandle[] = [];

		/** splice empties the array, so the two call sites cannot remove the same listener twice */
		const removeListeners = () =>
			listeners.splice(0).forEach((listener) => listener.remove());

		const setupListeners = async () => {
			listeners.push(
				// Active app listener
				await FirebaseMessaging.addListener('notificationReceived', (event) => {
					logger.debug('notificationReceived', { event });
					handlersRef.current.notificationHandler(
						event.notification.data as Payload
					);
				}),
				// Tray notification tap listener
				await FirebaseMessaging.addListener(
					'notificationActionPerformed',
					(event) => {
						logger.debug('notificationActionPerformed', { event });
						const payload = event.notification.data as Payload;

						handlersRef.current.notificationHandler(payload);
						handlersRef.current.navigateToPushTarget(payload);
					}
				)
			);

			/** Teardown ran while the listeners were still resolving - clean up after it */
			if (isUnmounted) {
				removeListeners();
			}
		};

		setupListeners();

		return () => {
			isUnmounted = true;
			removeListeners();
		};
	}, [logger]);

	return null;
}
